import { useState } from 'react'
import { Link } from 'react-router'
import type { MarketingVisit } from '@fitting-room/contracts'
import { OrderAmountLabel } from '../../components/admin/orders/VisitOrderAmounts'
import { useMarketingVisits } from '../../lib/adminQueries'
import { BOUTIQUE_LOCALE, boutiqueDateTime, boutiqueToday } from '../../lib/boutique'
import { formatDay } from '../../lib/format'
import { monthOf } from '../../lib/months'
import { useStaffToken } from '../../lib/staffToken'
import { uiStrings } from '../../lib/strings'

/**
 * The marketing partner's whole console: the appointments in a month, read
 * only. Everything here came from `GET /api/admin/marketing/visits`, which
 * has already cut the customer down to a first name and an initial and
 * masked their email and phone — this page has nothing to hide, because the
 * server never sent it. No drawer, no actions, no links into pages a
 * marketer is refused.
 */
export function MarketingVisitsPage() {
  const t = uiStrings(BOUTIQUE_LOCALE)
  const { token } = useStaffToken()
  const [month, setMonth] = useState(() => monthOf(boutiqueToday()))
  const visits = useMarketingVisits(token, month)

  return (
    <section>
      <h1 className="st-display mb-1 text-xl">{t.marketingTitle}</h1>
      <p className="st-meta mb-4 max-w-2xl">{t.marketingDescription}</p>
      <label className="mb-5 flex w-fit flex-col gap-1">
        <span className="st-eyebrow">{t.marketingMonth}</span>
        <input
          type="month"
          className="st-input"
          value={month}
          onChange={(event) => {
            if (/^\d{4}-\d{2}$/.test(event.target.value)) setMonth(event.target.value)
          }}
        />
      </label>

      {visits.isPending ? (
        <p className="st-meta">{t.marketingLoading}</p>
      ) : visits.isError ? (
        <p className="st-alert" role="alert">
          {t.marketingFailed}
        </p>
      ) : visits.data.visits.length === 0 ? (
        <p className="st-meta">{t.marketingEmpty}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="st-table" data-testid="marketing-visits">
            <thead>
              <tr>
                <th>{t.marketingColWhen}</th>
                <th>{t.marketingColCustomer}</th>
                <th>{t.marketingColContact}</th>
                <th>{t.marketingColType}</th>
                <th>{t.marketingColSource}</th>
                <th>{t.marketingColStylist}</th>
                <th>{t.marketingColWedding}</th>
                <th>{t.marketingColCameIn}</th>
                <th>{t.marketingColSold}</th>
                <th>{t.marketingColOrders}</th>
              </tr>
            </thead>
            <tbody>
              {visits.data.visits.map((visit) => (
                <MarketingRow key={visit.id} visit={visit} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}

function MarketingRow({ visit }: { readonly visit: MarketingVisit }) {
  const t = uiStrings(BOUTIQUE_LOCALE)
  return (
    <tr data-visit-id={visit.id}>
      <td className="whitespace-nowrap">{boutiqueDateTime(visit.scheduledAt)}</td>
      <td>{visit.customerShortName ?? t.marketingNone}</td>
      <td className="st-meta">
        {visit.maskedEmail || visit.maskedPhone ? (
          <>
            {visit.maskedEmail ? <span className="block">{visit.maskedEmail}</span> : null}
            {visit.maskedPhone ? <span className="block whitespace-nowrap">{visit.maskedPhone}</span> : null}
          </>
        ) : (
          t.marketingNone
        )}
      </td>
      <td>{visit.appointmentType}</td>
      <td>{visit.source ?? t.marketingNone}</td>
      <td>{visit.stylist ?? t.marketingNone}</td>
      <td className="whitespace-nowrap">{visit.weddingDate ? formatDay(visit.weddingDate, BOUTIQUE_LOCALE) : t.marketingNone}</td>
      <td data-testid="came-in">{t.marketingCameIn(visit.status)}</td>
      <td data-testid="sold">{visit.sold ? t.marketingSoldYes : t.marketingSoldNo}</td>
      <td>
        {visit.orders.length === 0
          ? t.marketingNone
          : visit.orders.map((order) => (
              <span key={order.orderNo} className="block whitespace-nowrap">
                <span className="tabular-nums">{order.orderNo}</span> · <OrderAmountLabel order={order} />
              </span>
            ))}
      </td>
    </tr>
  )
}

/**
 * Any other address under `/admin`, for a marketer: the server would refuse
 * every read the page makes, so the page is not mounted at all and this
 * says so instead of a screen full of 403s.
 */
export function MarketerNotHere() {
  const t = uiStrings(BOUTIQUE_LOCALE)
  return (
    <section className="st-card">
      <h1 className="st-display text-lg">{t.marketingNotHereTitle}</h1>
      <p className="st-meta mt-2">{t.marketingNotHereBody}</p>
      <Link to="/admin" className="st-btn-quiet mt-4 inline-flex">
        {t.marketingBack}
      </Link>
    </section>
  )
}
